import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { PaymentElement, CardElement as StripeCardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import Loader from 'components/general/Loader'
import config from 'config/api/Config'
import { onError } from 'config/lib/errorLib'
import ProviderAPI from 'config/api/ProviderAPI'

const paymentOptions = {
    defaultValues: {
        billingDetails: {
            address: {
                country: 'IE'
            }
        }
    }
}

export default function CardElement(props) {
    const {
        clientSecret,
        setPaymentMethod, // for setup mode success
        onSuccess,        // for payment mode success
        mode = 'setup',   // 'setup' | 'payment'
        cardOnly = false,
        submitLabel,      // optional override for the submit button text
        submitFullWidth = false // render a full-width block submit button
    } = props

    const stripe = useStripe()
    const elements = useElements()
    const api = new ProviderAPI()

    const [errorMessage, setErrorMessage] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [ready, setReady] = useState(false) // Stripe element fully rendered (onReady)

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!stripe || !elements) return
        setIsLoading(true)
        setErrorMessage(null)
        try {
            if (mode === 'setup') {
                const { setupIntent, error } = await stripe.confirmSetup({
                    elements,
                    confirmParams: { return_url: config.stripeReturnUrl },
                    redirect: 'if_required'
                })
                if (error) throw error
                if (setupIntent) {
                    const paymentMethodId = setupIntent.payment_method
                    setPaymentMethod && setPaymentMethod(paymentMethodId)
                }
            } else {
                if (cardOnly) {
                    const cardEl = elements.getElement(StripeCardElement)
                    const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
                        payment_method: { card: cardEl }
                    })
                    if (error) throw error
                    if (paymentIntent && paymentIntent.status === 'succeeded') {
                        onSuccess && onSuccess(paymentIntent.id)
                    }
                } else {
                    const { paymentIntent, error } = await stripe.confirmPayment({
                        elements,
                        redirect: 'if_required'
                    })
                    if (error) throw error
                    if (paymentIntent && paymentIntent.status === 'succeeded') {
                        onSuccess && onSuccess(paymentIntent.id)
                    }
                }
            }
        } catch (e) {
            setErrorMessage(e.message || 'Payment failed')
            onError(e)
        }
        setIsLoading(false)
    }
	
    return (
        <form onSubmit={handleSubmit} >
            {clientSecret ? (
                <Box style={{ position: 'relative', width: '100%' }}>
                    {/* Centered loader shown until Stripe reports the element is ready
                        (onReady). The element stays mounted underneath so it can
                        initialise; we just cover it to avoid a staged/partial render. */}
                    {!ready && (
                        <Box
                            display='flex'
                            alignItems='center'
                            justifyContent='center'
                            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: '#fff', zIndex: 2 }}
                        >
                            <Loader />
                        </Box>
                    )}

                    <Box style={{ minHeight: ready ? 0 : 200 }}>
                        <Box style={{marginBottom: '1rem'}}>
                            {(mode === 'setup' && !cardOnly) ? (
                                <PaymentElement
                                    options={paymentOptions}
                                    onReady={() => setReady(true)}
                                    onLoaderror={(e) => {
                                        setReady(true)
                                        setErrorMessage((e && e.error && e.error.message) || 'Unable to load the payment form. Please refresh and try again.')
                                    }}
                                />
                            ) : (
                                <Box p={1} border='1px solid #e2e8f0' borderRadius={4} style={{ background: '#fff' }}>
                                    <StripeCardElement
                                        onReady={() => setReady(true)}
                                        options={{
                                            style: {
                                                base: { fontSize: '16px', color: '#32325d', '::placeholder': { color: '#a0aec0' } },
                                                invalid: { color: '#e53e3e' }
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                            {errorMessage && <p className='red-text' style={{marginTop: '1rem'}}>{errorMessage}</p>}
                        </Box>

                        {ready && (
                            <Box style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: submitFullWidth ? 'center' : 'flex-end' }}>
                                <Button
                                    type='submit'
                                    color='primary'
                                    variant='contained'
                                    fullWidth={submitFullWidth}
                                    size={submitFullWidth ? 'large' : 'medium'}
                                    style={submitFullWidth ? { padding: '0.75rem', fontSize: '1rem' } : { marginLeft: 'auto' }}
                                    disabled={isLoading || !stripe || !elements}
                                >
                                    <span id='button-text'>
                                        {isLoading ? <Loader height={10} /> : (submitLabel || (mode === 'payment' ? 'Pay now' : 'Submit'))}
                                    </span>
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                <p className='red-text' style={{marginTop: '1rem'}}>Error loading Stripe, please refresh the page and try again</p>
            )}
        </form>
	)
}